import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route,useLocation } from "react-router-dom";
import { Layout, ConfigProvider, theme } from "antd";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import AuthPages from "./components/Authentication/AuthPage";
import ResumeLanding from "./components/main/ResumeLanding";
import ResumeBuilder from "./components/main/ResumeBuilder.jsx";
import AppHeader from "./components/main/AppHeader.jsx";
import ImportResume from "./components/main/ImportResume";
import ImportReview from "./components/main/ImportReview";
import WelcomePage from "./components/main/WelcomePage.jsx";
import TemplateGallery from "./components/main/TemplateGallery";
import FinalExport from "./components/NewResume/FinalExport";
import AppErrorBoundary from "./components/main/AppErrorBoundary";
import SubscriptionPage from "./components/Subscription/SubscriptionPage";
import { SubscriptionProvider } from "./components/Subscription/SubscriptionProvider";
import TemplateAuditPage from "./components/NewResume/TemplateAuditPage";
import PaymentAuditPage from "./components/NewResume/PaymentAuditPage";
import { useNavigate } from "react-router-dom";
import { useResumeStore } from "../src/store/resumeStore.jsx";
import { COLOR_THEME_TOKENS } from "./components/NewResume/colorThemes";
const { Content } = Layout;

/* ================= PROTECTED LAYOUT ================= */

const SESSION_DURATION = 24 * 60 * 60 * 1000;

function ProtectedLayout({ children, resumeData }) {
  const location = useLocation();
  const [themeMode, setThemeMode] = useState("light");
  const [userName, setUserName] = useState("");
  const colorTheme = useResumeStore((state) => state.selectedColorTheme);
  const setColorTheme = useResumeStore((state) => state.setSelectedColorTheme);
  const navigate = useNavigate();
  const auth = getAuth();

  /* ================= SESSION CHECK ================= */

  const isSessionExpired = () => {
    const loginTime = localStorage.getItem("authLoginTime");

    if (!loginTime) return true;

    return Date.now() - Number(loginTime) > SESSION_DURATION;
  };

  /* ================= SESSION REFRESH ================= */
  const refreshSession = () => {
    localStorage.setItem("authLoginTime", Date.now());
  };
  /* ================= GLOBAL AUTH GUARD ================= */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setUserName("");
        sessionStorage.removeItem("userName");
        navigate("/");
        return;
      }

      const nextUserName = user.displayName || "";
      setUserName(nextUserName);
      sessionStorage.setItem("userName", nextUserName);

      if (isSessionExpired()) {
        await signOut(auth);
        localStorage.removeItem("authLoginTime");
        navigate("/");
      }

    });
    return () => unsubscribe();
  }, [auth, navigate]);

  /* ================= ACTIVITY LISTENERS ================= */
  useEffect(() => {

    // Refresh session on activity
    window.addEventListener("click", refreshSession);
    window.addEventListener("keydown", refreshSession);
    window.addEventListener("mousemove", refreshSession);
    window.addEventListener("scroll", refreshSession);
    window.addEventListener("touchstart", refreshSession);

    // Check expiry every minute
    const interval = setInterval(async () => {
      if (isSessionExpired()) {
        await signOut(auth);
        localStorage.removeItem("authLoginTime");
        navigate("/");
      }
    }, 60000);

    return () => {
      clearInterval(interval);

      window.removeEventListener("click", refreshSession);
      window.removeEventListener("keydown", refreshSession);
      window.removeEventListener("mousemove", refreshSession);
      window.removeEventListener("scroll", refreshSession);
      window.removeEventListener("touchstart", refreshSession);
    };

  }, [auth, navigate]);

  /* ================= THEME TOGGLE ================= */

  const toggleTheme = () => {
    setThemeMode((prev) => (prev === "light" ? "dark" : "light"));
  };

  /* ================= UI ================= */

  const renderedChildren = React.isValidElement(children)
    ? React.cloneElement(children, {
        colorTheme,
        setColorTheme,
      })
    : children;

  return (
    <ConfigProvider
      theme={{
        algorithm:
          themeMode === "dark"
            ? theme.darkAlgorithm
            : theme.defaultAlgorithm,

        token: {
          ...(COLOR_THEME_TOKENS[colorTheme] || {})
        }
      }}
    >
      <Layout style={{ minHeight: "100vh" }}>
        {location?.pathname !=="/dashboard" &&
          location?.pathname !== "/welcome" &&
          <AppHeader
            resumeData={resumeData}
            theme={themeMode}
            onToggleTheme={toggleTheme}
            setColorTheme={setColorTheme}
            colorTheme={colorTheme}
          userName={userName}
        />}

        <Content
          style={{
            background: theme.colorPrimaryBg,
            // padding: "0px 10px",
          }}
        >
          {renderedChildren}
        </Content>
      </Layout>
    </ConfigProvider>
  );
}

/* ================= MAIN APP ================= */

function App() {
  const resumeData = useResumeStore((s) => s.resumeData);
  const updateField = useResumeStore((s) => s.updateField);

  return (
    <AppErrorBoundary>
      <SubscriptionProvider>
        <BrowserRouter>
          <Routes>
          {/* ========== PUBLIC ========== */}
          <Route path="/" element={<AuthPages />} />
          {(process.env.NODE_ENV !== "production" ||
            process.env.REACT_APP_TEMPLATE_AUDIT === "1") && (
            <Route path="/__template-audit" element={<TemplateAuditPage />} />
          )}
          {process.env.NODE_ENV !== "production" && (
            <Route path="/__payment-audit" element={<PaymentAuditPage />} />
          )}

          {/* ========== DASHBOARD ========== */}
          <Route
            path="/dashboard"
            element={
              <ProtectedLayout resumeData={resumeData}>
                <ResumeLanding />
              </ProtectedLayout>
            }
          />

          {/* ========== CREATE / EDIT RESUME ========== */}
          <Route
            path="/resume/new"
            element={
              <ProtectedLayout resumeData={resumeData}>
                <ResumeBuilder
                  resumeData={resumeData}
                  setResumeData={updateField}
                />
              </ProtectedLayout>
            }
          />

          {/* ========== WELCOME ========== */}
          <Route
            path="/welcome"
            element={
              <ProtectedLayout resumeData={resumeData}>
                <WelcomePage />
              </ProtectedLayout>
            }
          />

          {/* ========== RESUME LIST ========== */}
          <Route
            path="/resume/list"
            element={
              <ProtectedLayout resumeData={resumeData}>
                <div>My Resumes (Coming Soon)</div>
              </ProtectedLayout>
            }
          />

          {/* ========== IMPORT ========== */}
          <Route
            path="/resume/import"
            element={
              <ProtectedLayout resumeData={resumeData}>
                <ImportResume />
              </ProtectedLayout>
            }
          />

          <Route
            path="/resume/review"
            element={
              <ProtectedLayout resumeData={resumeData}>
                <ImportReview />
              </ProtectedLayout>
            }
          />

          {/* ========== TEMPLATES ========== */}
          <Route
            path="/templates"
            element={
              <ProtectedLayout resumeData={resumeData}>
                <TemplateGallery />
              </ProtectedLayout>
            }
          />

          <Route
            path="/subscription"
            element={
              <ProtectedLayout resumeData={resumeData}>
                <SubscriptionPage />
              </ProtectedLayout>
            }
          />

          {/* ========== EXPORT / PREVIEW ========== */}
          <Route
            path="/export"
            element={
              <ProtectedLayout resumeData={resumeData}>
                <FinalExport resumeData={resumeData} />
              </ProtectedLayout>
            }
          />
          </Routes>
        </BrowserRouter>
      </SubscriptionProvider>
    </AppErrorBoundary>
  );
}

export default App;
